import * as React from 'react';

import type {
    DripSequenceFragment as DripSequence,
    SmsDripSequenceFragment as SmsDripSequence,
    EditionFragment as DripEdition,
    SmsDripEditionFragment as SmsDripEdition,
    TimespanInput,
    StartTriggerSetInput,
    StartTriggerSetInputFieldInput,
} from 'nutshell-graphql-types';

import {AddStartTriggerForm} from '../sidebar/add-start-trigger-form';
import type {TriggerTypeValue} from '../sidebar/constants';
import {DripSequenceSidebarForm} from '../sidebar/drip-sequence-sidebar-form';
import {formDataToTriggerConditionsArray} from '../sidebar/helpers';
import {StartTriggerCard} from '../start-trigger/start-trigger-card';

import type {Variant} from './color-icon-variants';

type Props = {
    isLoading: boolean;
    dripSequence: DripSequence | SmsDripSequence | null | undefined;
    editions: DripEdition[] | SmsDripEdition[];
    isEditingStartTrigger: string | null | undefined;
    setIsEditingStartTrigger: (arg1?: string | null | undefined) => void;
    initialDelay: TimespanInput | null | undefined;
    variant?: Variant;
    isTriggerEditable: boolean;
    triggerTypeValues: TriggerTypeValue[];
    onSetStartTrigger: (input: StartTriggerSetInput) => Promise<any>;
    onEditionUpdate: (
        editionId: string,
        sendDelay?: TimespanInput | null | undefined
    ) => Promise<any>;
};

export function StartTriggerCardWrapper(props: Props) {
    const dripSequence = props.dripSequence;

    const [optionalDelay, setOptionalDelay] = React.useState<TimespanInput | null | undefined>(
        null
    );
    // If true will render EditActiveDelayConfirmation which will query and show confirmation if needed
    const [shouldCheckForEditDelayConfirmation, setShouldCheckForEditDelayConfirmation] =
        React.useState<boolean>(false);
    // Tracks other form variables that may have been changed when waiting on user to confirm/cancel delay changes
    const [otherFormVariablesToSave, setOtherFormVariablesToSave] = React.useState<
        StartTriggerSetInputFieldInput | null | undefined
    >(undefined);

    const [isStopOnLeadClosed, setIsStopOnLeadClosed] = React.useState<boolean>(false);
    const [isStopOnRemovedFromAudience, setIsStopOnRemovedFromAudience] =
        React.useState<boolean>(false);
    const [isSaving, setIsSaving] = React.useState<boolean>(false);
    const [saveFailedErrorMessage, setSaveFailedErrorMessage] = React.useState<
        string | null | undefined
    >(undefined);

    React.useEffect(() => {
        // Because of the way this component is, the form was persisting wrong delay on open and close
        if (props.isEditingStartTrigger && !isSaving) {
            setOptionalDelay(props.initialDelay);
        }
    }, [props.initialDelay, props.isEditingStartTrigger, isSaving]);

    React.useEffect(() => {
        setIsStopOnLeadClosed(
            dripSequence && dripSequence.isStopOnLeadClose
                ? Boolean(dripSequence.isStopOnLeadClose)
                : false
        );

        setIsStopOnRemovedFromAudience(
            dripSequence && dripSequence.isStopOnRemovedFromAudience
                ? Boolean(dripSequence.isStopOnRemovedFromAudience)
                : false
        );
    }, [dripSequence]);

    const saveStartTrigger = (
        dripSequenceId: string,
        variables?: StartTriggerSetInputFieldInput | null,
        delay?: TimespanInput | null
    ): Promise<any> => {
        const setStartTriggerPromise = props.onSetStartTrigger({
            campaignId: dripSequenceId,
            startTrigger: variables,
        });

        const updateEditionWithDelayPromise =
            props.editions && props.editions.length && props.onEditionUpdate
                ? // @ts-expect-error
                  props.onEditionUpdate(props.editions[0].id, delay)
                : undefined;

        return Promise.all([setStartTriggerPromise, updateEditionWithDelayPromise])
            .then(() => {
                props.setIsEditingStartTrigger(null);
            })
            .catch((error) => {
                setSaveFailedErrorMessage(error.message);
            });
    };

    const delayCouldNeedConfirmation = () => {
        // Only need to do this if the sequence is currently active and not an SMS campaign
        if (
            dripSequence &&
            (dripSequence.status !== 'ACTIVE' || dripSequence.__typename === 'SmsCampaign')
        )
            return false;

        if (props.initialDelay && optionalDelay) {
            // Delay unit was changed
            if (props.initialDelay.unit !== optionalDelay.unit) return true;
            // Delay timespan was changed
            if (props.initialDelay.timespan !== optionalDelay.timespan) return true;
        } else if (props.initialDelay && !optionalDelay) {
            // Delay was deleted
            return true;
        }

        // Delay was unchanged or new delay was added
        return false;
    };

    return (
        <div className='mb-16'>
            <StartTriggerCard
                isLoading={props.isLoading}
                startTrigger={dripSequence ? dripSequence.startTrigger : undefined}
                isActive={props.isTriggerEditable}
                onClick={
                    props.isTriggerEditable
                        ? (startTriggerId: string) => props.setIsEditingStartTrigger(startTriggerId)
                        : undefined
                }
                variant={props.variant}
            />
            {props.isEditingStartTrigger && dripSequence ? (
                <DripSequenceSidebarForm
                    title={dripSequence.startTrigger ? 'Edit trigger' : 'Add trigger'}
                    // @ts-expect-error
                    onSubmit={(formData) => {
                        if (!formData.triggerType) return;

                        // Hacky prevent submit until NUT-14546 saves the day with real form validation/error messages
                        if (optionalDelay && optionalDelay.timespan === 0) return;

                        const variables = {
                            type: formData.triggerType,
                            conditions: formDataToTriggerConditionsArray(formData),
                            isStopOnLeadClose: isStopOnLeadClosed,
                            isStopOnRemovedFromAudience: isStopOnRemovedFromAudience,
                            removedFromAudienceIds: formData.audiences || [],
                        } as const;

                        if (delayCouldNeedConfirmation()) {
                            // Necessary until this can be moved into a promise or something so redux will listen
                            setIsSaving(true);
                            setShouldCheckForEditDelayConfirmation(true);
                            // Record other form variables that may need to be saved/updated if user confirms save
                            setOtherFormVariablesToSave(variables);
                        } else {
                            // Redux form only sets 'submitting' state if onSubmit returns a promise
                            return saveStartTrigger(dripSequence.id, variables, optionalDelay);
                        }
                    }}
                    onClose={() => {
                        setSaveFailedErrorMessage(undefined);
                        setOtherFormVariablesToSave(undefined);
                        props.setIsEditingStartTrigger(null);
                    }}
                    isNewItem={Boolean(!dripSequence || !dripSequence.startTrigger)}
                    existingStartTrigger={
                        dripSequence.startTrigger ? dripSequence.startTrigger : undefined
                    }
                    isSaving={isSaving}
                    saveFailedErrorMessage={saveFailedErrorMessage}
                    // @ts-expect-error
                    deleteProps={
                        dripSequence && dripSequence.startTrigger
                            ? {
                                  title: 'Remove start trigger',
                                  buttonText: 'Remove start trigger',
                                  message: 'Are you sure you’d like to remove this start trigger?',
                                  onDelete: () => {
                                      setIsSaving(true);
                                      setOptionalDelay(null);
                                      saveStartTrigger(dripSequence.id, null, null).then(() => {
                                          setIsSaving(false);
                                      });
                                  },
                              }
                            : undefined
                    }
                >
                    <AddStartTriggerForm
                        isStopOnLeadClosed={isStopOnLeadClosed}
                        // @ts-expect-error
                        setIsStopOnLeadClosed={(newValue) => setIsStopOnLeadClosed(newValue)}
                        isStopOnRemovedFromAudience={isStopOnRemovedFromAudience}
                        setIsStopOnRemovedFromAudience={setIsStopOnRemovedFromAudience}
                        delay={optionalDelay}
                        setOptionalDelay={
                            // @ts-expect-error
                            props.editions.length ? (delay) => setOptionalDelay(delay) : undefined
                        }
                        isLoading={props.isLoading}
                        initialDelay={props.initialDelay}
                        firstEdition={props.editions.length ? props.editions[0] : undefined}
                        shouldCheckForEditDelayConfirmation={shouldCheckForEditDelayConfirmation}
                        onConfirmSaveChanges={() => {
                            setShouldCheckForEditDelayConfirmation(false);

                            saveStartTrigger(
                                dripSequence.id,
                                otherFormVariablesToSave,
                                optionalDelay
                            ).then(() => {
                                setIsSaving(false);
                            });
                        }}
                        onCancelChanges={() => {
                            setShouldCheckForEditDelayConfirmation(false);
                            setIsSaving(false);
                            setOtherFormVariablesToSave(undefined);
                        }}
                        triggerTypeValues={props.triggerTypeValues}
                    />
                </DripSequenceSidebarForm>
            ) : undefined}
        </div>
    );
}
